import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, MessageSquareHeart } from 'lucide-react';

import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { formatDate } from '../../../utils/Utils';
import { NOT_APPLICABLE, SURVEY_QUESTIONS, useMyExperience } from '../config/experienceApi';

// PM analitik P7b — E19 çalışan kartı: sonuç görüşmesi durumu + "Görüşmeyi Yaptık" teyidi + gömülü 3 soruluk süreç deneyimi anketi.
// Teyit güncel sonuç SÜRÜMÜNE bağlıdır ve puanı kabul etmek DEĞİLDİR (onay penceresinde açıkça yazılır). Sonuç yeniden
// yayımlanırsa eski teyit eski sürümde kalır; kart yeni görüşme/teyit gerekliliğini gösterir. Anket tek gönderimdir ve
// anonimdir (cevap kişiye bağlanmaz). Sonuç yayımlanmamışsa kart hiç görünmez.
const Card = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgColor};
  color: ${({ theme }) => theme.colors.primary.main};
`;

const Body = styled.div`
  flex: 1;
  min-width: 220px;
`;

const Title = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Line = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const Warn = styled(Line)`
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

export default function ResultMeetingCard({ periodId }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const {
    data, confirm, submitSurvey,
  } = useMyExperience(periodId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);

  const scaleOptions = useMemo(() => [
    ...[5, 4, 3, 2, 1].map((v) => ({ id: String(v), name: t(`performance.experience.scale.${v}`) })),
    { id: NOT_APPLICABLE, name: t('performance.experience.scale.na') },
  ], [t]);

  const schema = useMemo(() => [
    { name: 'notice', type: 'notice', content: t('performance.experience.my.surveyNotice'), grid: 12 },
    ...SURVEY_QUESTIONS.map((q) => ({
      name: q, label: t(`performance.experience.question.${q}.text`), type: 'select', grid: 12, options: scaleOptions, idKey: 'id', labelKey: 'name',
    })),
  ], [t, scaleOptions]);

  if (!data || !data.participant || !data.resultPublished) return null;

  const meeting = data.meeting || {};
  let meetingLine;
  if (meeting.status === 'held') meetingLine = t('performance.experience.my.meetingHeld', { date: formatDate(meeting.heldAt, lang) });
  else if (meeting.status === 'planned') meetingLine = t('performance.experience.my.meetingPlanned', { date: formatDate(meeting.plannedAt, lang) });
  else meetingLine = t('performance.experience.my.meetingNotPlanned', { date: formatDate(meeting.dueAt, lang) });

  return (
    <>
      <Card data-card="result-meeting">
        <IconWrap><CalendarCheck size={20} /></IconWrap>
        <Body>
          <Title>{t('performance.experience.my.title')}</Title>
          <Line data-line="meeting">{meetingLine}</Line>
          {data.republished ? <Warn data-line="republished">{t('performance.experience.my.republished')}</Warn> : null}
          {data.confirmedAt
            ? <Line data-line="confirmed">{t('performance.experience.my.confirmedAt', { date: formatDate(data.confirmedAt, lang) })}</Line>
            : null}
          {!data.confirmedAt && data.previousVersionConfirmedAt
            ? <Line data-line="previous">{t('performance.experience.my.previousConfirmed', { date: formatDate(data.previousVersionConfirmedAt, lang) })}</Line>
            : null}
          <Line>{t('performance.experience.my.notAcceptance')}</Line>
        </Body>
        <Actions>
          {!data.confirmedAt ? (
            <Button variant="outline" onClick={() => setConfirmOpen(true)} data-action="confirm-meeting">
              {t('performance.experience.my.confirm')}
            </Button>
          ) : null}
        </Actions>
      </Card>

      {data.surveyEligible ? (
        <Card data-card="experience-survey">
          <IconWrap><MessageSquareHeart size={20} /></IconWrap>
          <Body>
            <Title>{t('performance.experience.my.surveyTitle')}</Title>
            <Line>
              {data.surveySubmitted
                ? t('performance.experience.my.surveySubmitted', { date: formatDate(data.surveySubmittedAt, lang) })
                : t('performance.experience.my.surveyInvite')}
            </Line>
          </Body>
          {!data.surveySubmitted ? (
            <Actions>
              <Button onClick={() => setSurveyOpen(true)} data-action="open-survey">{t('performance.experience.my.surveyOpen')}</Button>
            </Actions>
          ) : null}
        </Card>
      ) : null}

      <ConfirmDialog
        isOpen={confirmOpen}
        type="info"
        title={t('performance.experience.my.confirmTitle')}
        message={t('performance.experience.my.confirmMessage')}
        confirmText={t('performance.experience.my.confirm')}
        loading={saving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setSaving(true);
          const ok = await confirm();
          setSaving(false);
          if (ok) setConfirmOpen(false);
        }}
      />

      <GenericFormDrawer
        open={surveyOpen}
        onClose={() => setSurveyOpen(false)}
        title={t('performance.experience.my.surveyTitle')}
        schema={schema}
        submitLabel={t('performance.experience.my.surveySubmit')}
        onSave={async (values) => submitSurvey(values)}
      />
    </>
  );
}
